import pick from 'lodash/pick';

import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  transactionLineItems,
  sendMessage as requestSendMessage,
  initiateConversation,
} from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { findNextBoundary, getStartOf, monthIdString } from '../../util/dates';
import { isUserAuthorized } from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import { omitInvalidCategoryParams, searchValidListingTypes } from '../SearchPage/SearchPage.duck';
import { resultIds } from '../../util/listings';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_RELATED_LISTINGS_REQUEST = 'app/ListingPage/FETCH_RELATED_LISTINGS_REQUEST';
export const FETCH_RELATED_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_RELATED_LISTINGS_SUCCESS';
export const FETCH_RELATED_LISTINGS_ERROR = 'app/ListingPage/FETCH_RELATED_LISTINGS_ERROR';

export const FETCH_SELLER_LISTINGS_REQUEST = 'app/ListingPage/FETCH_SELLER_LISTINGS_REQUEST';
export const FETCH_SELLER_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_SELLER_LISTINGS_SUCCESS';
export const FETCH_SELLER_LISTINGS_ERROR = 'app/ListingPage/FETCH_SELLER_LISTINGS_ERROR';

export const FETCH_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_TIME_SLOTS_REQUEST';
export const FETCH_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_TIME_SLOTS_SUCCESS';
export const FETCH_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  relatedListings: [],
  relatedListingsPagination: null,
  fetchRelatedListingsInProgress: null,
  fetchRelatedListingsError: null,
  sellerListings: [],
  sellerListingsPagination: null,
  fetchSellerListingsError: null,
  fetchSellerListingsInProgress: null,
  reviewsPagination: null,
  reviewsInProgress: false,
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null, reviewsInProgress: true };
    case FETCH_REVIEWS_SUCCESS:
      return {
        ...state,
        reviews: [...state.reviews, ...payload.data],
        reviewsInProgress: false,
        reviewsPagination: payload.meta,
      };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload, reviewsInProgress: false };

    case FETCH_RELATED_LISTINGS_REQUEST:
      return {
        ...state,
        fetchRelatedListingsError: null,
        fetchRelatedListingsInProgress: true,
      };
    case FETCH_RELATED_LISTINGS_SUCCESS:
      return {
        ...state,
        relatedListings: [...state.relatedListings, ...resultIds(payload.data)],
        relatedListingsPagination: payload.data.meta,
        fetchRelatedListingsError: null,
        fetchRelatedListingsInProgress: false,
      };
    case FETCH_RELATED_LISTINGS_ERROR:
      return {
        ...state,
        fetchRelatedListingsError: payload,
        fetchRelatedListingsInProgress: false,
        relatedListings: [],
      };

    case FETCH_SELLER_LISTINGS_REQUEST:
      return {
        ...state,
        fetchSellerListingsError: null,
        fetchSellerListingsInProgress: true,
      };
    case FETCH_SELLER_LISTINGS_SUCCESS:
      return {
        ...state,
        sellerListings: [...state.sellerListings, ...resultIds(payload.data)],
        sellerListingsPagination: payload.data.meta,
        fetchSellerListingsError: null,
        fetchSellerListingsInProgress: false,
      };
    case FETCH_SELLER_LISTINGS_ERROR:
      return {
        ...state,
        fetchSellerListingsError: payload,
        fetchSellerListingsInProgress: false,
        sellerListings: [],
      };

    case FETCH_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false, inquiryModalOpenForListingId: null };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchRelatedListingsRequest = () => ({ type: FETCH_RELATED_LISTINGS_REQUEST });
export const fetchRelatedListingsSuccess = response => ({
  type: FETCH_RELATED_LISTINGS_SUCCESS,
  payload: { data: response.data },
});
export const fetchRelatedListingsError = error => ({
  type: FETCH_RELATED_LISTINGS_ERROR,
  error: true,
  payload: error,
});

export const fetchSellerListingsRequest = () => ({ type: FETCH_SELLER_LISTINGS_REQUEST });
export const fetchSellerListingsSuccess = response => ({
  type: FETCH_SELLER_LISTINGS_SUCCESS,
  payload: { data: response.data },
});
export const fetchSellerListingsError = error => ({
  type: FETCH_SELLER_LISTINGS_ERROR,
  error: true,
  payload: error,
});

export const fetchTimeSlotsRequest = monthId => ({
  type: FETCH_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchTimeSlotsError = (monthId, error) => ({
  type: FETCH_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

// ================ Thunks ================ //

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));
  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: [
      'author',
      'author.profileImage',
      'author.publicData.isHolidayMode',
      'images',
      'currentStock',
      'metadata.wishlistCount',
    ],
    'fields.image': [
      // Scaled variants for large images
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      return data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

export const fetchReviews = (userId, params = {}) => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      subject_id: userId,
      page: 1,
      perPage: 10,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
      ...params,
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess({ data: reviews, meta: response.data.meta }));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone) => (dispatch, getState, sdk) => {
  const monthId = monthIdString(start, timeZone);

  dispatch(fetchTimeSlotsRequest(monthId));

  // The maximum pagination page size for timeSlots is 500
  const extraParams = {
    perPage: 500,
    page: 1,
  };

  return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
    .then(timeSlots => {
      dispatch(fetchTimeSlotsSuccess(monthId, timeSlots));
    })
    .catch(e => {
      dispatch(fetchTimeSlotsError(monthId, storableError(e)));
    });
};

export const searchListings = (listingId, type, searchParams, config) => (
  dispatch,
  getState,
  sdk
) => {
  const fetchBasedOnType = {
    seller: {
      request: fetchSellerListingsRequest,
      success: fetchSellerListingsSuccess,
      error: fetchSellerListingsError,
    },
    related: {
      request: fetchRelatedListingsRequest,
      success: fetchRelatedListingsSuccess,
      error: fetchRelatedListingsError,
    },
  };

  dispatch(fetchBasedOnType[type].request());

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas

  const aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card';
  const aspectRatio = aspectHeight / aspectWidth;
  const params = {
    // The rest of the params except invalid nested category-related params
    // Note: invalid independent search params are still passed through
    ...omitInvalidCategoryParams(
      {
        page: 1,
        include: ['author', 'images'],
        'fields.listing': [
          'title',
          'geolocation',
          'price',
          'deleted',
          'state',
          'publicData.listingType',
          'publicData.transactionProcessAlias',
          'publicData.unitType',
          // These help rendering of 'purchase' listings,
          // when transitioning from search page to listing page
          'publicData.pickupEnabled',
          'publicData.shippingEnabled',
        ],
        'fields.user': [
          'profile.displayName',
          'profile.abbreviatedName',
          'profile.publicData.isHolidayMode',
        ],
        'fields.image': [
          'variants.scaled-small',
          'variants.scaled-medium',
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
        // Ensure that the listing is instock
        minStock: 1,
        stockMode: 'match-undefined',
        ...searchParams,
      },
      config
    ),
    ...searchValidListingTypes(config),
  };
  return sdk.listings
    .query(params)
    .then(response => {
      const listings = response.data.data.filter(listing => listing.id.uuid !== listingId.uuid);
      const newResponse = { ...response, data: { ...response.data, data: listings } };
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(newResponse, sanitizeConfig));
      if (type === 'related') {
        dispatch(fetchBasedOnType[type].success(newResponse));
      } else {
        dispatch(fetchBasedOnType[type].success(response));
      }
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(fetchBasedOnType[type].error(error));
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

export const sendInquiry = (listing, message) => async (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  try {
    const listingId = listing?.id;
    const conversation = await initiateConversation({
      productId: listingId.uuid,
    });

    await requestSendMessage({
      conversationId: Number(conversation.id),
      text: message,
    });
    dispatch(sendInquirySuccess());
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
  } catch (e) {
    dispatch(sendInquiryError(storableError(e)));
    throw e;
  }
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const unitType = attributes?.publicData?.unitType;
    const timeUnit = unitType === 'hour' ? 'hour' : 'day';
    const nextBoundary = findNextBoundary(new Date(), timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonth, tz)),
      dispatch(fetchTimeSlots(listing.id, nextMonth, nextAfterNextMonth, tz)),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

const getCategoryLevels = publicData =>
  Object.keys(publicData || {}).reduce((levels, key) => {
    if (key.startsWith('categoryLevel') && publicData[key]) {
      const index = key.replace('categoryLevel', '');
      levels[`pub_categoryLevel${index}`] = publicData[key];
    }
    return levels;
  }, {});

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        orderData,
      });
    });
};

export const fetchSellerListingsBasedOnListing = (listingId, searchParams, config) =>
  searchListings(listingId, 'seller', searchParams, config);

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId = isUserAuthorized(currentUser)
    ? state.ListingPage.inquiryModalOpenForListingId
    : null;

  // Clear old line-items
  dispatch(setInitialValues({ lineItems: null, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true));
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);

  if (!canFetchData) {
    return Promise.resolve();
  }

  return Promise.all([
    dispatch(showListing(listingId, config)),
    config.deliveryListingId && dispatch(showListing(config.deliveryListingId, config)),
  ]).then(response => {
    const listingResponse = response[0];
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    const listingAuthorId = listing?.relationships?.author?.data?.id.uuid;

    Promise.all([
      dispatch(fetchReviews(listingAuthorId)),
      dispatch(
        searchListings(
          listingId,
          'related',
          {
            ...getCategoryLevels(listing?.attributes?.publicData),
            perPage: 10,
            page: 1,
          },
          config
        )
      ),
      dispatch(
        fetchSellerListingsBasedOnListing(
          listingId,
          {
            authorId: listingAuthorId,
            perPage: 7,
            page: 1,
          },
          config
        )
      ),
    ]);

    if (isBookingProcessAlias(transactionProcessAlias)) {
      // Fetch timeSlots.
      // This can happen parallel to loadData.
      // We are not interested to return them from loadData call.
      fetchMonthlyTimeSlots(dispatch, listing);
    }
    return response;
  });
};
